import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Drawer from './components/Drawer';
import BlueCartDrawer from './components/BlueCartDrawer';
import Footer from './components/Footer';
import AuthBootstrap from './components/auth/AuthBootstrap';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CookieBanner from './components/cookies/CookieBanner';
import { BlueCartProvider } from './context/BlueCartContext';

import HomePage                    from './pages/HomePage';
import AboutPage                   from './pages/AboutPage';
import MeetOurCEOPage              from './pages/MeetOurCEOPage';
import CatalogPage                 from './pages/CatalogPage';
import ProductDetailPage           from './pages/ProductDetailPage';
import SubmitRFQPage               from './pages/SubmitRFQPage';
import RFQConfirmationPage         from './pages/RFQConfirmationPage';
import SignInPage                  from './pages/SignInPage';
import SignUpPage                  from './pages/SignUpPage';
import AuthCallbackPage            from './pages/AuthCallbackPage';
import AccountPage                 from './pages/AccountPage';
import CookiePolicyPage            from './pages/CookiePolicyPage';
import WarrantyPage                from './pages/WarrantyPage';
import TechnicalDataSheetPage      from './pages/TechnicalDataSheetPage';
import InstallationInstructionsPage from './pages/InstallationInstructionsPage';

// ── Page Loader — full-screen splash on initial mount ──────────────────────
function PageLoader({ visible }) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      background: '#161512',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 32,
      opacity: visible ? 1 : 0,
      pointerEvents: visible ? 'all' : 'none',
      transition: 'opacity 0.55s cubic-bezier(0.16,1,0.3,1)',
    }}>
      {/* Wordmark */}
      <div style={{ textAlign: 'center' }}>
        <p style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: 13,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.35)',
          marginBottom: 10,
        }}>Contractor Rob</p>
        <p style={{
          fontFamily: '"Inter Tight", system-ui, sans-serif',
          fontSize: 10,
          letterSpacing: '0.38em',
          textTransform: 'uppercase',
          color: 'rgba(232,179,57,0.7)',
          fontWeight: 500,
        }}>Loading Catalog</p>
      </div>

      {/* Spinner ring */}
      <div style={{ position: 'relative', width: 44, height: 44 }}>
        <svg
          viewBox="0 0 44 44"
          width="44" height="44"
          style={{ animation: 'cr-spin 1.1s linear infinite' }}>
          <circle
            cx="22" cy="22" r="18"
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="2"
          />
          <circle
            cx="22" cy="22" r="18"
            fill="none"
            stroke="#E8B339"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="30 83"
            style={{ animation: 'cr-dash 1.1s ease-in-out infinite' }}
          />
        </svg>
      </div>

      <style>{`
        @keyframes cr-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes cr-dash {
          0%   { stroke-dashoffset: 0; }
          50%  { stroke-dashoffset: -60; }
          100% { stroke-dashoffset: -113; }
        }
      `}</style>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // 'instant' explicitly bypasses smooth-scroll CSS — prevents animated
    // scroll-to-top on route change regardless of scroll-behavior settings.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

export default function App() {
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [loaderVisible,  setLoaderVisible]  = useState(true);

  useEffect(() => {
    // Show loader briefly then fade out — gives fonts + images a head start
    const t = setTimeout(() => setLoaderVisible(false), 1100);
    return () => clearTimeout(t);
  }, []);

  return (
    <Router>
      <BlueCartProvider>
        <PageLoader visible={loaderVisible} />
        <AuthBootstrap/>
        <ScrollToTop/>
        <Navbar onDrawerOpen={()=>setDrawerOpen(true)} onCartDrawerOpen={()=>setCartDrawerOpen(true)}/>
        <Drawer open={drawerOpen} onClose={()=>setDrawerOpen(false)}/>
        <BlueCartDrawer open={cartDrawerOpen} onClose={()=>setCartDrawerOpen(false)}/>

        <Routes>
          <Route path="/"      element={<HomePage/>}/>
          <Route path="/about" element={<AboutPage/>}/>
          <Route path="/about/ceo" element={<MeetOurCEOPage/>}/>
          <Route path="/catalog"      element={<CatalogPage/>}/>
          <Route path="/products/:id" element={<ProductDetailPage/>}/>
          <Route path="/rfq/submit"       element={<SubmitRFQPage/>}/>
          <Route path="/rfq/confirmation" element={<RFQConfirmationPage/>}/>
          <Route path="/signin"        element={<SignInPage/>}/>
          <Route path="/signup"        element={<SignUpPage/>}/>
          <Route path="/auth/callback" element={<AuthCallbackPage/>}/>
          <Route path="/account"          element={<ProtectedRoute><AccountPage/></ProtectedRoute>}/>
          <Route path="/account/quotes"   element={<ProtectedRoute><AccountPage/></ProtectedRoute>}/>
          <Route path="/account/settings" element={<ProtectedRoute><AccountPage/></ProtectedRoute>}/>
          <Route path="/cookies" element={<CookiePolicyPage/>}/>
          {/* Technical Documents */}
          <Route path="/technical/warranty"                  element={<WarrantyPage/>}/>
          <Route path="/technical/data-sheet"                element={<TechnicalDataSheetPage/>}/>
          <Route path="/technical/installation-instructions" element={<InstallationInstructionsPage/>}/>
        </Routes>

        <Footer/>
        <CookieBanner/>
      </BlueCartProvider>
    </Router>
  );
}
